import { Nullable } from "@core/domain/types/nullable.type";
import { LoadLayoutStore } from "@core/presentacion/component/feedback/load-layout/load-layout.store";
import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { FinancialEntityBudget } from "@project/domain/models/budget/financial-entity-budget.model";
import { EditFinancialEntityBudgetModel } from "@project/domain/models/budget/edit-financial-entity-budget.model";
import { EditBudgetUseCase } from "@project/domain/usecases/budget/edit-budget.usecase";
import { GetBudgetByIdUseCase } from "@project/domain/usecases/budget/get-budget-by-id.usecase";
import { FetchJustificationTemplatesUseCase } from "@project/domain/usecases/budget/fetch-justification-templates-usecase";
import { FinancialEntityBudgetFormValuesValidated } from "@project/presentation/components/budget-form/budget-form.component";
import { inject, injectable } from "inversify";
import { makeObservable, runInAction } from "mobx";

export interface JustificationTemplateOption {
    id: number;
    name: string;
}

@injectable()
export class EditBudgetTemplatePageViewModel extends BaseViewModel {
    budgetId: Nullable<number> = null;
    private isTemplateMode = false;

    budget: Nullable<FinancialEntityBudget> = null;
    initialLoading = false;
    justificationTemplates: JustificationTemplateOption[] = [];
    loadingJustificationTemplates = false;

    constructor(
        @inject(GetBudgetByIdUseCase)
        private readonly getBudgetByIdUseCase: GetBudgetByIdUseCase,
        @inject(EditBudgetUseCase)
        private readonly editBudgetUseCase: EditBudgetUseCase,
        @inject(FetchJustificationTemplatesUseCase)
        private readonly fetchJustificationTemplatesUseCase: FetchJustificationTemplatesUseCase,
    ) {
        super();
        makeObservable(this, {
            budget: true,
            initialLoading: true,
            justificationTemplates: true,
            loadingJustificationTemplates: true,
            fetchBudget: false,
            fetchJustificationTemplates: false,
        });
    }

    setTemplateMode(isTemplate: boolean): void {
        this.isTemplateMode = isTemplate;
    }

    override async didMount(): Promise<void> {
        if (this.budgetId) {
            await Promise.all([
                this.fetchBudget(),
                this.fetchJustificationTemplates(),
            ]);
        }
    }

    async fetchBudget(): Promise<void> {
        if (!this.budgetId) return;

        this.initialLoading = true;
        try {
            const budget = await this.getBudgetByIdUseCase.execute(this.budgetId);
            runInAction(() => {
                this.budget = budget;
            });
        } finally {
            runInAction(() => {
                this.initialLoading = false;
            });
        }
    }

    async fetchJustificationTemplates(): Promise<void> {
        this.loadingJustificationTemplates = true;
        try {
            const templates = await this.fetchJustificationTemplatesUseCase.execute();
            runInAction(() => {
                this.justificationTemplates = templates;
            });
        } finally {
            runInAction(() => {
                this.loadingJustificationTemplates = false;
            });
        }
    }

    async editBudget(
        budgetData: FinancialEntityBudgetFormValuesValidated & { id: number },
    ): Promise<boolean> {
        LoadLayoutStore.start();

        try {
            const editModel = new EditFinancialEntityBudgetModel(
                budgetData.id,
                budgetData.name,
                budgetData.description,
                this.isTemplateMode ? null : budgetData.financialEntityId || null,
                budgetData.justificationTemplateId,
                this.isTemplateMode
            );

            const result = await this.editBudgetUseCase.execute(editModel);

            if (result) {
                ToastManagerStore.success();
                return true;
            }
            return false;
        } finally {
            LoadLayoutStore.finish();
        }
    }
}
